import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { dateShort, money, moneySigned } from "../lib/format";
import CoverageGaps from "../components/CoverageGaps";
import StatusBadge from "../components/StatusBadge";
import TrackingDetailSheet from "../components/TrackingDetailSheet";
import NoteSelect from "../components/NoteSelect";
import BucketLinesSheet from "../components/BucketLinesSheet";
import DraftDialog from "../components/DraftDialog";
import PeriodSummary from "../components/PeriodSummary";
import { toast } from "sonner";
import {
  AlertTriangle,
  ArrowLeft,
  Loader2,
  RefreshCw,
  TrendingDown,
  TrendingUp,
  Boxes,
  AlertCircle,
  Search,
  Upload,
  FileText,
  X,
  Plus,
  Mail,
  RotateCcw,
  ShieldAlert,
  Trash2,
  Power,
  Download,
} from "lucide-react";

const TABS = [
  { id: "flagged", label: "Flagged" },
  { id: "overcharge", label: "Overcharges" },
  { id: "missing_from_cms", label: "Missing from CMS" },
  { id: "missing_from_ups", label: "Not yet billed" },
  { id: "prp_pending", label: "PRP pending" },
  { id: "matched", label: "Matched" },
  { id: "all", label: "All" },
];

export default function ReportDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("flagged");
  const [loadingItems, setLoadingItems] = useState(false);
  const [reconciling, setReconciling] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedTracking, setSelectedTracking] = useState(null);
  const [pendingCms, setPendingCms] = useState([]);
  const [pendingUps, setPendingUps] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [draftOpen, setDraftOpen] = useState(false);
  const [draftText, setDraftText] = useState("");
  const [draftLoading, setDraftLoading] = useState(false);
  const [activeRange, setActiveRange] = useState({
    from: null,
    to: null,
    period: "MTD",
  });
  const [bucketOpen, setBucketOpen] = useState(null);
  const [dangerOpen, setDangerOpen] = useState(null); // 'clear' | 'disable' | null

  const load = async () => {
    try {
      const data = await api.getReport(id);
      setReport(data);
      return data;
    } catch (e) {
      toast.error("Failed to load report");
    }
  };

  const loadItems = async (f, range = activeRange) => {
    setLoadingItems(true);
    try {
      const data = await api.listItems(id, f, range.from, range.to);
      setItems(data);
    } catch (e) {
      toast.error("Failed to load items");
    } finally {
      setLoadingItems(false);
    }
  };

  useEffect(() => {
    load();
  }, [id]);

  useEffect(() => {
    if (report?.status === "ready") loadItems(filter, activeRange);
    setSelected(new Set());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, report?.status, activeRange.from, activeRange.to]);

  // Poll while reconciliation is running in the background. Stops as soon as
  // status flips to ready/error.
  useEffect(() => {
    if (report?.status !== "extracting") return;
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      try {
        const r = await api.getReport(id);
        if (cancelled) return;
        setReport(r);
        if (r.status === "ready") {
          await loadItems(filter, activeRange);
          toast.success("Reconciliation complete");
        } else if (r.status === "error") {
          toast.error(r.error_message || "Reconciliation failed");
        }
      } catch (e) {
        /* swallow — keep polling */
      }
    };
    const interval = setInterval(tick, 2500);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [report?.status, id]);

  const handleRangeChange = useCallback((from, to, period) => {
    setActiveRange((prev) => {
      if (prev.from === from && prev.to === to && prev.period === period) {
        return prev;
      }
      return { from, to, period };
    });
  }, []);

  const handleReconcile = async () => {
    setReconciling(true);
    try {
      // Upload any pending files first
      if (pendingCms.length) {
        await api.uploadFiles(id, "cms", pendingCms);
      }
      if (pendingUps.length) {
        await api.uploadFiles(id, "ups", pendingUps);
      }
      const newFiles = pendingCms.length + pendingUps.length;
      setPendingCms([]);
      setPendingUps([]);

      const updated = await api.reconcile(id);
      setReport(updated);
      toast.success(
        newFiles > 0
          ? `Reconciliation queued · ${newFiles} new file${newFiles === 1 ? "" : "s"}`
          : "Reconciliation queued"
      );
    } catch (e) {
      const msg = e?.response?.data?.detail || "Reconciliation failed to start";
      toast.error(msg);
    } finally {
      setReconciling(false);
    }
  };

  const filtered = useMemo(() => {
    if (!search.trim()) return items;
    const q = search.trim().toUpperCase();
    return items.filter((i) => i.tracking_number.includes(q));
  }, [items, search]);

  const toggleSelect = (track) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(track)) next.delete(track);
      else next.add(track);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size >= filtered.length && filtered.length > 0) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filtered.map((i) => i.tracking_number)));
    }
  };

  const handleSaveNote = async (tracking, note) => {
    try {
      await api.updateNote(id, tracking, note);
      setItems((prev) =>
        prev.map((it) =>
          it.tracking_number === tracking ? { ...it, user_note: note } : it
        )
      );
      toast.success(note ? `Note set: ${note}` : "Note cleared");
    } catch (e) {
      toast.error("Couldn't save note");
    }
  };

  const handleGenerateDraft = async () => {
    if (selected.size === 0) return;
    setDraftLoading(true);
    setDraftOpen(true);
    try {
      const res = await api.disputeDraft(id, Array.from(selected));
      setDraftText(res.draft || "");
    } catch (e) {
      toast.error("Couldn't generate draft");
      setDraftOpen(false);
    } finally {
      setDraftLoading(false);
    }
  };

  if (!report) {
    return (
      <div className="text-sm text-[#525252] flex items-center gap-2" data-testid="loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading report…
      </div>
    );
  }

  const flagged =
    (report.summary?.overcharge_count || 0) +
    (report.summary?.missing_from_cms_count || 0);

  return (
    <div data-testid="report-detail-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-5 mb-8">
        <div>
          <div className="eyebrow mb-2">{report.period}</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold leading-tight">
            {report.name}
          </h1>
          <p className="text-xs text-[#525252] font-mono-tabular mt-2">
            {report.cms_files.length} CMS file · {report.ups_files.length} UPS invoice file{report.ups_files.length === 1 ? "" : "s"}
            {report.reconciled_at && ` · reconciled ${dateShort(report.reconciled_at)}`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setDangerOpen("menu")}
            className="inline-flex items-center gap-2 px-3 py-2 border border-[#E5E5E5] bg-white hover:bg-[#FAFAFA] text-sm rounded-md text-[#525252]"
            title="Account & data"
            data-testid="account-menu-btn"
          >
            <ShieldAlert className="w-4 h-4" />
            <span className="hidden md:inline">Account</span>
          </button>
          <button
            onClick={handleReconcile}
            disabled={reconciling}
            className="inline-flex items-center gap-2 px-4 py-2 border border-[#E5E5E5] bg-white hover:bg-[#FAFAFA] text-sm font-medium rounded-md disabled:opacity-50"
            data-testid="rerun-reconcile-btn"
          >
            {reconciling ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4" />
            )}
            Re-run reconciliation
          </button>
        </div>
      </div>

      {report.status === "error" && (
        <div
          className="mb-8 p-4 border border-[#FECACA] bg-[#FFF0F0] rounded-md text-sm text-[#DC2626] flex gap-3"
          data-testid="error-banner"
        >
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            <div className="font-medium">Reconciliation failed</div>
            <div className="text-xs mt-1">{report.error_message}</div>
          </div>
        </div>
      )}

      {report.status === "extracting" && (
        <ExtractingBanner progress={report.progress} />
      )}

      <section className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10" data-testid="kpi-grid">
        <KpiCard
          icon={<TrendingUp className="w-4 h-4" />}
          label="Charged to customers"
          value={money(report.summary?.total_charged_customer)}
          testId="kpi-charged"
        />
        <KpiCard
          icon={<TrendingDown className="w-4 h-4" />}
          label="UPS billed you"
          value={money(report.summary?.total_ups_cost)}
          testId="kpi-ups-cost"
        />
        <KpiCard
          icon={
            (report.summary?.total_profit || 0) < 0 ? (
              <TrendingDown className="w-4 h-4 text-[#DC2626]" />
            ) : (
              <TrendingUp className="w-4 h-4 text-[#16A34A]" />
            )
          }
          label="Net profit"
          value={moneySigned(report.summary?.total_profit)}
          accent={(report.summary?.total_profit || 0) < 0 ? "red" : "green"}
          testId="kpi-profit"
        />
        <KpiCard
          icon={<AlertCircle className="w-4 h-4 text-[#DC2626]" />}
          label="Flagged shipments"
          value={String(flagged)}
          accent={flagged > 0 ? "red" : undefined}
          testId="kpi-flagged"
        />
      </section>

      <section className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-10">
        <MiniStat
          label="CMS shipments"
          value={report.summary?.total_cms_shipments || 0}
        />
        <MiniStat
          label="UPS billed (unique)"
          value={report.summary?.total_ups_invoice_lines || 0}
        />
        <MiniStat
          label="Overcharges"
          value={report.summary?.overcharge_count || 0}
          warn
        />
        <MiniStat
          label="Missing from CMS"
          value={report.summary?.missing_from_cms_count || 0}
          warn
        />
        <MiniStat
          label="PRP pending"
          value={report.summary?.prp_pending_count || 0}
        />
      </section>

      <AddFilesPanel
        report={report}
        pendingCms={pendingCms}
        pendingUps={pendingUps}
        setPendingCms={setPendingCms}
        setPendingUps={setPendingUps}
        onReconcile={handleReconcile}
        reconciling={reconciling}
        onAfterReprocess={async () => {
          await load();
          await loadItems(filter, activeRange);
        }}
      />

      <PeriodSummary
        reportId={id}
        refreshKey={report.reconciled_at || ""}
        onRangeChange={handleRangeChange}
        onBucketClick={(b) => setBucketOpen(b)}
      />

      <section className="bg-white border border-[#E5E5E5] rounded-md overflow-hidden">
        <div className="px-4 py-3 border-b border-[#E5E5E5] bg-[#FAFAFA] flex items-center justify-between gap-3 flex-wrap text-xs" data-testid="items-range-banner">
          <div className="text-[#525252]">
            Showing tracking numbers with ship date or invoice date in{" "}
            <span className="font-mono-tabular text-[#0A0A0A] font-medium">
              {activeRange.period}
            </span>
            {activeRange.from && activeRange.to ? (
              <>
                {" "}·{" "}
                <span className="font-mono-tabular text-[#0A0A0A]">
                  {dateShort(activeRange.from)} → {dateShort(activeRange.to)}
                </span>
              </>
            ) : activeRange.period === "CUSTOM" ? (
              <span className="italic"> (pick a range above)</span>
            ) : null}
          </div>
          <span className="font-mono-tabular text-[#525252]">
            {items.length} item{items.length === 1 ? "" : "s"}
          </span>
        </div>
        <div className="border-b border-[#E5E5E5] flex items-center justify-between gap-4 px-2 md:px-4">
          <div className="flex overflow-x-auto scroll-thin" role="tablist">
            {TABS.map((t) => {
              const count =
                t.id === "flagged"
                  ? flagged
                  : t.id === "overcharge"
                  ? report.summary?.overcharge_count
                  : t.id === "missing_from_cms"
                  ? report.summary?.missing_from_cms_count
                  : t.id === "missing_from_ups"
                  ? report.summary?.missing_from_ups_count
                  : t.id === "prp_pending"
                  ? report.summary?.prp_pending_count
                  : t.id === "matched"
                  ? report.summary?.matched_count
                  : null;
              const active = filter === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => setFilter(t.id)}
                  className={`px-4 py-3 text-sm whitespace-nowrap border-b-2 transition ${
                    active
                      ? "border-[#0A0A0A] text-[#0A0A0A] font-medium"
                      : "border-transparent text-[#525252] hover:text-[#0A0A0A]"
                  }`}
                  data-testid={`tab-${t.id}`}
                  role="tab"
                  aria-selected={active}
                >
                  {t.label}
                  {count !== null && count !== undefined && (
                    <span className="ml-2 font-mono-tabular text-xs text-[#525252]">
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
          <div className="hidden md:flex items-center gap-3 pr-3">
            <div className="flex items-center gap-2">
              <Search className="w-3.5 h-3.5 text-[#525252]" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Tracking #…"
                className="font-mono-tabular text-xs bg-transparent border-none focus:outline-none w-44"
                data-testid="tracking-search-input"
              />
            </div>
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/reports/${id}/items.csv?filter=${filter}${
                activeRange.from && activeRange.to
                  ? `&range_from=${activeRange.from}&range_to=${activeRange.to}`
                  : ""
              }`}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs border border-[#E5E5E5] hover:bg-[#0A0A0A] hover:text-white hover:border-[#0A0A0A] rounded-md transition"
              data-testid="download-csv-btn"
              title="Download current view as CSV"
            >
              <Download className="w-3 h-3" /> CSV
            </a>
          </div>
        </div>

        {report.status !== "ready" ? (
          <div
            className="p-12 text-center text-sm text-[#525252]"
            data-testid="not-ready"
          >
            Reconciliation hasn&apos;t completed yet.
          </div>
        ) : loadingItems ? (
          <div className="p-12 text-center text-sm text-[#525252] flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : filtered.length === 0 ? (
          <div
            className="p-12 text-center text-sm text-[#525252]"
            data-testid="items-empty"
          >
            <Boxes className="w-6 h-6 mx-auto mb-3 text-[#A3A3A3]" />
            No items in this view.
          </div>
        ) : (
          <div className="overflow-x-auto scroll-thin">
            <table className="w-full data-table text-sm" data-testid="items-table">
              <thead>
                <tr>
                  <th className="px-3 py-3 w-10">
                    <input
                      type="checkbox"
                      checked={
                        filtered.length > 0 && selected.size >= filtered.length
                      }
                      onChange={toggleSelectAll}
                      className="cursor-pointer accent-[#0A0A0A]"
                      aria-label="Select all"
                      data-testid="select-all-checkbox"
                    />
                  </th>
                  <th className="px-5 py-3 text-left">Tracking #</th>
                  <th className="px-5 py-3 text-left">Ship date</th>
                  <th className="px-5 py-3 text-right">CMS charge</th>
                  <th className="px-5 py-3 text-right">UPS billed</th>
                  <th className="px-5 py-3 text-right">Profit / loss</th>
                  <th className="px-5 py-3 text-left">Status</th>
                  <th className="px-5 py-3 text-left">Note</th>
                  <th className="px-5 py-3 text-left">Invoiced on</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((it) => {
                  const isLoss =
                    typeof it.profit === "number" && it.profit < 0;
                  const isChecked = selected.has(it.tracking_number);
                  return (
                    <tr
                      key={it.id}
                      className={`border-t border-[#E5E5E5] cursor-pointer hover:bg-[#FAFAFA] ${
                        isChecked ? "bg-[#FAFAFA]" : ""
                      }`}
                      onClick={() => setSelectedTracking(it.tracking_number)}
                      data-testid={`item-row-${it.tracking_number}`}
                    >
                      <td
                        className="px-3 py-3"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => toggleSelect(it.tracking_number)}
                          className="cursor-pointer accent-[#0A0A0A]"
                          aria-label={`Select ${it.tracking_number}`}
                          data-testid={`row-checkbox-${it.tracking_number}`}
                        />
                      </td>
                      <td className="px-5 py-3 font-mono-tabular text-[13px] font-medium text-[#0A0A0A] underline-offset-2 hover:underline">
                        {it.tracking_number}
                      </td>
                      <td className="px-5 py-3 font-mono-tabular text-xs text-[#525252]">
                        {dateShort(it.ship_date)}
                      </td>
                      <td className="px-5 py-3 text-right font-mono-tabular">
                        {money(it.cms_charge)}
                      </td>
                      <td className="px-5 py-3 text-right font-mono-tabular">
                        {money(it.ups_billed_charge)}
                      </td>
                      <td
                        className={`px-5 py-3 text-right font-mono-tabular ${
                          isLoss ? "text-[#DC2626] font-medium" : ""
                        }`}
                      >
                        {moneySigned(it.profit)}
                      </td>
                      <td className="px-5 py-3">
                        <StatusBadge status={it.status} />
                      </td>
                      <td
                        className="px-5 py-3"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <NoteSelect
                          value={it.user_note}
                          onChange={(note) =>
                            handleSaveNote(it.tracking_number, note)
                          }
                          testId={`note-select-${it.tracking_number}`}
                          compact
                        />
                      </td>
                      <td className="px-5 py-3 text-xs text-[#525252] font-mono-tabular">
                        {it.ups_invoice_dates?.length
                          ? it.ups_invoice_dates.join(", ")
                          : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {selected.size > 0 && (
        <div
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 bg-[#0A0A0A] text-white rounded-md shadow-xl border border-[#0A0A0A] px-4 py-3 flex items-center gap-4"
          data-testid="selection-toolbar"
        >
          <span className="text-xs font-mono-tabular">
            {selected.size} selected
          </span>
          <button
            onClick={() => setSelected(new Set())}
            className="text-xs text-[#A3A3A3] hover:text-white"
            data-testid="clear-selection-btn"
          >
            Clear
          </button>
          <div className="w-px h-5 bg-[#404040]"></div>
          <button
            onClick={handleGenerateDraft}
            className="inline-flex items-center gap-2 px-3 py-1.5 bg-white text-[#0A0A0A] text-sm font-medium rounded-sm hover:bg-[#FAFAFA]"
            data-testid="generate-draft-btn"
          >
            <Mail className="w-4 h-4" /> Generate dispute draft
          </button>
        </div>
      )}

      <DraftDialog
        open={draftOpen}
        draft={draftText}
        loading={draftLoading}
        onClose={() => setDraftOpen(false)}
      />

      <BucketLinesSheet
        reportId={id}
        bucket={bucketOpen}
        range={activeRange}
        onClose={() => setBucketOpen(null)}
      />

      <DangerZone
        report={report}
        onCleared={async () => {
          await load();
          await loadItems(filter, activeRange);
        }}
        open={dangerOpen}
        setOpen={setDangerOpen}
      />

      <TrackingDetailSheet
        reportId={id}
        tracking={selectedTracking}
        onClose={() => setSelectedTracking(null)}
        onNoteChange={(track, note) => {
          setItems((prev) =>
            prev.map((it) =>
              it.tracking_number === track ? { ...it, user_note: note } : it,
            ),
          );
        }}
      />
    </div>
  );
}

function KpiCard({ icon, label, value, accent, testId }) {
  const accentCls =
    accent === "red"
      ? "text-[#DC2626]"
      : accent === "green"
      ? "text-[#16A34A]"
      : "text-[#0A0A0A]";
  return (
    <div
      className="bg-white border border-[#E5E5E5] rounded-md p-5"
      data-testid={testId}
    >
      <div className="flex items-center justify-between mb-3">
        <span className="eyebrow">{label}</span>
        <span className="text-[#525252]">{icon}</span>
      </div>
      <div className={`font-display text-2xl md:text-3xl font-bold font-mono-tabular ${accentCls}`}>
        {value}
      </div>
    </div>
  );
}

function MiniStat({ label, value, warn }) {
  return (
    <div className="bg-white border border-[#E5E5E5] rounded-md px-4 py-3 flex items-center justify-between">
      <span className="eyebrow">{label}</span>
      <span
        className={`font-mono-tabular text-sm font-medium ${
          warn && value > 0 ? "text-[#DC2626]" : "text-[#0A0A0A]"
        }`}
      >
        {value}      </span>
    </div>
  );
}

function AddFilesPanel({
  report,
  pendingCms,
  pendingUps,
  setPendingCms,
  setPendingUps,
  onReconcile,
  reconciling,
  onAfterReprocess,
}) {
  const cmsRef = useRef(null);
  const upsRef = useRef(null);
  const [processed, setProcessed] = useState(null);
  const [reprocessing, setReprocessing] = useState(null);

  const loadProcessed = () =>
    api
      .listProcessedFiles(report.id)
      .then(setProcessed)
      .catch(() => setProcessed({ cms: [], ups: [] }));

  useEffect(() => {
    loadProcessed();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [report.id, report.reconciled_at]);

  const handleReprocess = async (filename, kind) => {
    if (
      !window.confirm(
        `Re-process "${filename}"? Its rows will be wiped and re-extracted.`,
      )
    )
      return;
    setReprocessing(filename);
    try {
      await api.reprocessFile(report.id, filename, kind);
      toast.success(`Re-processed ${filename}`);
      await loadProcessed();
      onAfterReprocess?.();
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || `Couldn't re-process ${filename}`,
      );
    } finally {
      setReprocessing(null);
    }
  };

  const onCmsSelected = (list) => {
    const arr = Array.from(list || []).filter((f) =>
      f.name.toLowerCase().endsWith(".pdf"),
    );
    setPendingCms([...pendingCms, ...arr]);
  };
  const onUpsSelected = (list) => {
    const arr = Array.from(list || []).filter((f) => {
      const n = f.name.toLowerCase();
      return n.endsWith(".pdf") || n.endsWith(".csv");
    });
    setPendingUps([...pendingUps, ...arr]);
  };
  const pendingNew = pendingCms.length + pendingUps.length;
  return (
    <section
      className="bg-white border border-[#E5E5E5] rounded-md p-5 mb-8"
      data-testid="add-files-panel"
    >
      <div className="flex items-start justify-between gap-4 mb-5 flex-wrap">
        <div>
          <div className="eyebrow mb-1">Continuous ledger</div>
          <div className="text-sm">
            Drop in new CMS exports or UPS invoices as you receive them, then
            re-run reconciliation. Already-processed files are skipped.
          </div>
        </div>
        {pendingNew > 0 && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm border border-[#FEF08A] bg-[#FEFCE8] text-[#CA8A04] text-xs font-mono-tabular">
            <Plus className="w-3 h-3" /> {pendingNew} new to process
          </span>
        )}
      </div>

      <ProcessedList
        processed={processed}
        report={report}
        onReprocess={handleReprocess}
        reprocessing={reprocessing}
      />

      <CoverageGaps reportId={report.id} refreshKey={report.reconciled_at || ""} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
        <PendingDrop
          label="Add CMS report PDFs"
          accept="application/pdf"
          extensions={[".pdf"]}
          files={pendingCms}
          onFiles={onCmsSelected}
          onRemove={(i) =>
            setPendingCms(pendingCms.filter((_, idx) => idx !== i))
          }
          inputRef={cmsRef}
          testId="add-cms-drop"
        />
        <PendingDrop
          label="Add UPS invoices (PDF or CSV)"
          accept="application/pdf,text/csv,.csv"
          extensions={[".pdf", ".csv"]}
          files={pendingUps}
          onFiles={onUpsSelected}
          onRemove={(i) =>
            setPendingUps(pendingUps.filter((_, idx) => idx !== i))
          }
          inputRef={upsRef}
          testId="add-ups-drop"
        />
      </div>
      <div className="flex items-center justify-end mt-4 pt-4 border-t border-[#E5E5E5]">
        <button
          onClick={onReconcile}
          disabled={reconciling}
          className="inline-flex items-center gap-2 px-4 py-2 bg-[#0A0A0A] text-white text-sm font-medium rounded-md hover:bg-[#171717] disabled:opacity-50 transition active:scale-[0.98]"
          data-testid="add-and-reconcile-btn"
        >
          {reconciling ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" /> Reconciling…
            </>
          ) : pendingNew > 0 ? (
            <>
              <Upload className="w-4 h-4" /> Upload {pendingNew} & reconcile
            </>
          ) : (
            <>
              <RefreshCw className="w-4 h-4" /> Re-run reconciliation
            </>
          )}
        </button>
      </div>
    </section>
  );
}

function ProcessedList({ processed, report, onReprocess, reprocessing }) {
  if (!processed) {
    return (
      <div className="text-xs text-[#525252] flex items-center gap-2">
        <Loader2 className="w-3 h-3 animate-spin" /> Loading processed files…
      </div>
    );
  }
  const cms = processed.cms || [];
  const ups = processed.ups || [];
  // Fallback: for legacy CMS rows with no source_file, use the single file
  // listed on the report (works when there's exactly one CMS file).
  const fallbackCms =
    (report?.cms_files || []).length === 1 ? report.cms_files[0] : null;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="processed-list">
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="eyebrow">CMS reports processed</div>
          <span className="text-[11px] font-mono-tabular text-[#525252]">
            {cms.length}
          </span>
        </div>
        {cms.length === 0 ? (
          <div className="text-xs text-[#525252] border border-dashed border-[#E5E5E5] rounded-sm px-3 py-2">
            No CMS reports yet.
          </div>
        ) : (
          <ul className="space-y-1.5" data-testid="processed-cms-list">
            {cms.map((c, i) => {
              const fname =
                c.source_file && c.source_file !== "—"
                  ? c.source_file
                  : fallbackCms;
              const busy = reprocessing === fname;
              return (
                <li
                  key={i}
                  className="border border-[#E5E5E5] rounded-sm px-3 py-2 text-xs"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono-tabular text-[11px] text-[#525252] truncate">
                      {fname || c.source_file}
                    </span>
                    <span className="flex items-center gap-2 shrink-0">
                      <span className="font-mono-tabular text-[11px] text-[#525252]">
                        {c.row_count} rows
                      </span>
                      {fname && (
                        <button
                          onClick={() => onReprocess(fname, "cms")}
                          disabled={busy}
                          className="p-1 rounded-sm hover:bg-[#F0F0EF] disabled:opacity-50"
                          title="Re-process this file"
                          aria-label="Re-process this file"
                          data-testid={`reprocess-cms-${i}`}
                        >
                          {busy ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            <RotateCcw className="w-3 h-3 text-[#525252]" />
                          )}
                        </button>
                      )}
                    </span>
                  </div>
                  <div className="font-mono-tabular text-[12px] mt-1 text-[#0A0A0A]">
                    {dateShort(c.min_date)}
                    {c.max_date && c.max_date !== c.min_date
                      ? ` → ${dateShort(c.max_date)}`
                      : ""}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="eyebrow">UPS invoices processed</div>
          <span className="text-[11px] font-mono-tabular text-[#525252]">
            {ups.length}
          </span>
        </div>
        {ups.length === 0 ? (
          <div className="text-xs text-[#525252] border border-dashed border-[#E5E5E5] rounded-sm px-3 py-2">
            No UPS invoices yet.
          </div>
        ) : (
          <ul
            className="space-y-1.5 max-h-44 overflow-y-auto scroll-thin pr-1"
            data-testid="processed-ups-list"
          >
            {ups.map((u, i) => {
              const fname = (u.source_files || []).find((s) => s && s !== "—");
              const busy = reprocessing === fname;
              return (
                <li
                  key={i}
                  className="border border-[#E5E5E5] rounded-sm px-3 py-2 text-xs flex items-center justify-between gap-2"
                >
                  <span className="font-mono-tabular text-[12px] truncate">
                    {u.invoice_number}
                  </span>
                  <span className="flex items-center gap-2 shrink-0">
                    <span className="font-mono-tabular text-[11px] text-[#525252]">
                      {dateShort(u.invoice_date)} · {u.row_count} lines
                    </span>
                    {fname && (
                      <button
                        onClick={() => onReprocess(fname, "ups")}
                        disabled={busy}
                        className="p-1 rounded-sm hover:bg-[#F0F0EF] disabled:opacity-50"
                        title="Re-process this file"
                        aria-label="Re-process this file"
                        data-testid={`reprocess-ups-${i}`}
                      >
                        {busy ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <RotateCcw className="w-3 h-3 text-[#525252]" />
                        )}
                      </button>
                    )}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

function PendingDrop({ label, files, onFiles, onRemove, inputRef, testId, accept = "application/pdf", extensions = [".pdf"] }) {
  const [drag, setDrag] = useState(false);
  return (
    <div>
      <div className="eyebrow mb-2">{label}</div>
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDrag(true);
        }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDrag(false);
          onFiles(e.dataTransfer.files);
        }}
        className={`dropzone w-full rounded-md p-5 text-left ${drag ? "active" : ""}`}
        data-testid={testId}
      >
        <Upload className="w-4 h-4 text-[#525252] mb-2" />
        <div className="text-xs font-medium">
          Drop {extensions.join(" or ")} or click to browse
        </div>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={accept}
          className="hidden"
          onChange={(e) => onFiles(e.target.files)}
          data-testid={`${testId}-input`}
        />
      </button>
      {files.length > 0 && (
        <ul className="mt-2 space-y-1">
          {files.map((f, i) => (
            <li
              key={i}
              className="flex items-center justify-between text-xs bg-[#FAFAFA] border border-[#E5E5E5] rounded-sm px-2 py-1.5"
            >
              <span className="flex items-center gap-1.5 truncate">
                <FileText className="w-3 h-3 text-[#525252] shrink-0" />
                <span className="truncate font-mono-tabular">{f.name}</span>
              </span>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove(i);
                }}
                className="text-[#525252] hover:text-[#DC2626]"
                aria-label="Remove"
              >
                <X className="w-3 h-3" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}


function ExtractingBanner({ progress }) {
  const phase = progress?.phase || "Working";
  const current = progress?.current ?? 0;
  const total = progress?.total ?? 0;
  const pct = total > 0 ? Math.min(100, Math.round((current / total) * 100)) : null;
  return (
    <div
      className="mb-8 border border-[#FEF08A] bg-[#FEFCE8] rounded-md overflow-hidden"
      data-testid="extracting-banner"
    >
      <div className="p-4 text-sm text-[#854D0E] flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2.5">
          <Loader2 className="w-4 h-4 animate-spin text-[#CA8A04]" />
          <div>
            <div className="font-medium" data-testid="progress-phase">
              {phase}
              {total > 0 && (
                <span className="ml-2 font-mono-tabular text-[#A16207]">
                  {current}/{total}
                </span>
              )}
            </div>
            <div className="text-xs text-[#A16207] mt-0.5">
              You can leave this page open — we'll keep working in the background.
            </div>
          </div>
        </div>
        {pct !== null && (
          <span
            className="font-mono-tabular text-sm font-medium text-[#854D0E]"
            data-testid="progress-pct"
          >
            {pct}%
          </span>
        )}
      </div>
      <div className="h-1.5 bg-[#FEF3C7]">
        <div
          className={`h-full bg-[#CA8A04] ${pct === null ? "animate-pulse w-1/3" : "transition-all duration-500"}`}
          style={pct !== null ? { width: `${pct}%` } : undefined}
        />
      </div>
    </div>
  );
}


function DangerZone({ report, onCleared, open, setOpen }) {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);

  if (!open) return null;
  const isAdmin = user?.role === "admin";

  const close = () => {
    setOpen(null);
    setConfirmText("");
  };

  const clearAll = async () => {
    setBusy(true);
    try {
      await api.clearReportData(report.id, confirmText);
      toast.success("All data cleared. You can start fresh.");
      close();
      await onCleared?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't clear data");
    } finally {
      setBusy(false);
    }
  };

  const disableAccount = async () => {
    setBusy(true);
    try {
      await api.disableAccount(confirmText);
      toast.success("Account disabled. Sorry to see you go.");
      await logout();
      navigate("/login", { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't disable account");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-[#0A0A0A]/40 grid place-items-center px-4"
      onClick={close}
      data-testid="danger-modal"
    >
      <div
        className="w-full max-w-md bg-white rounded-md border border-[#E5E5E5] p-6"
        onClick={(e) => e.stopPropagation()}
      >
        {open === "menu" && (
          <div>
            <h3 className="font-display text-xl font-bold mb-1">Account & data</h3>
            <p className="text-xs text-[#525252] mb-5">
              Manage what's stored under your store.
            </p>
            <div className="space-y-3">
              <button
                onClick={() => setOpen("clear")}
                className="w-full text-left border border-[#E5E5E5] rounded-md p-4 hover:bg-[#FAFAFA]"
                data-testid="open-clear-data"
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="font-medium text-sm">Clear all data</span>
                  <Trash2Icon />
                </div>
                <div className="text-xs text-[#525252]">
                  Wipes every CMS report, UPS invoice, and reconciliation
                  result. Your account stays active so you can re-upload.
                </div>
              </button>
              {!isAdmin && (
                <button
                  onClick={() => setOpen("disable")}
                  className="w-full text-left border border-[#FECACA] bg-[#FFF8F8] rounded-md p-4 hover:bg-[#FFF0F0]"
                  data-testid="open-disable-account"
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium text-sm text-[#DC2626]">
                      Disable my account
                    </span>
                    <PowerIcon />
                  </div>
                  <div className="text-xs text-[#525252]">
                    Wipes all data and deactivates your account. You'll be
                    signed out. Admin can re-enable.
                  </div>
                </button>
              )}
            </div>
            <button
              onClick={close}
              className="w-full mt-5 px-3 py-2 text-sm text-[#525252] hover:bg-[#FAFAFA] rounded-md"
              data-testid="close-account-menu"
            >
              Cancel
            </button>
          </div>
        )}

        {open === "clear" && (
          <ConfirmForm
            title="Clear all data"
            description={
              <>
                This permanently deletes every CMS export, UPS invoice, and
                reconciliation line currently on this report. The report
                itself stays so you can re-upload fresh files.
              </>
            }
            expected={user?.store_number}
            label={`Type your store number (${user?.store_number}) to confirm`}
            confirmText={confirmText}
            setConfirmText={setConfirmText}
            busy={busy}
            onCancel={close}
            onConfirm={clearAll}
            confirmCta="Wipe data"
            testIdPrefix="clear"
          />
        )}

        {open === "disable" && (
          <ConfirmForm
            title="Disable my account"
            description={
              <>
                Wipes every CMS export, UPS invoice, and reconciliation line.
                Deactivates your account and signs you out. Your admin can
                re-enable later if needed.
              </>
            }
            expected={user?.email}
            label={`Type your email (${user?.email}) to confirm`}
            confirmText={confirmText}
            setConfirmText={setConfirmText}
            busy={busy}
            onCancel={close}
            onConfirm={disableAccount}
            confirmCta="Disable account"
            testIdPrefix="disable"
          />
        )}
      </div>
    </div>
  );
}

function ConfirmForm({
  title,
  description,
  expected,
  label,
  confirmText,
  setConfirmText,
  busy,
  onCancel,
  onConfirm,
  confirmCta,
  testIdPrefix,
}) {
  const matches =
    expected && confirmText.trim().toLowerCase() === expected.trim().toLowerCase();
  return (
    <div>
      <h3 className="font-display text-xl font-bold mb-1 text-[#DC2626]">
        {title}
      </h3>
      <p className="text-xs text-[#525252] mb-4 leading-relaxed">
        {description}
      </p>
      <label className="block eyebrow mb-1.5" htmlFor={`${testIdPrefix}-confirm`}>
        {label}
      </label>
      <input
        id={`${testIdPrefix}-confirm`}
        value={confirmText}
        onChange={(e) => setConfirmText(e.target.value)}
        className="w-full border border-[#E5E5E5] rounded-md px-3 py-2 text-sm font-mono-tabular focus:border-[#0A0A0A] focus:outline-none"
        data-testid={`${testIdPrefix}-confirm-input`}
      />
      <div className="flex items-center justify-end gap-2 mt-5">
        <button
          onClick={onCancel}
          className="px-3 py-1.5 text-sm text-[#525252] hover:bg-[#FAFAFA] rounded-md"
          data-testid={`${testIdPrefix}-cancel`}
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          disabled={!matches || busy}
          className="inline-flex items-center gap-2 px-4 py-2 bg-[#DC2626] hover:bg-[#B91C1C] text-white text-sm font-medium rounded-md disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid={`${testIdPrefix}-confirm-btn`}
        >
          {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
          {confirmCta}
        </button>
      </div>
    </div>
  );
}

function Trash2Icon() {
  return <Trash2 className="w-4 h-4 text-[#525252]" />;
}

function PowerIcon() {
  return <Power className="w-4 h-4 text-[#DC2626]" />;
}
